import { computed, type ComputedRef, reactive } from 'vue'

export type RequestPermissionsState = PermissionState | 'pending' | 'unsupported'

export function useRequestPermissions(): ComputedRef<RequestPermissionsState> {
  const permissions = reactive({
    audio: 'pending' satisfies RequestPermissionsState,
    camera: 'pending' satisfies RequestPermissionsState,
  })

  // FIXME: this arrow is never invoked, so `getPermission` never runs and both
  // permissions stay `'pending'` for the life of the composable. Calling it would change
  // behaviour, so it is left alone here and raised separately.
  // oxlint-disable-next-line no-unused-expressions
  ;async () => {
    permissions.audio = await getPermission('audio')
    permissions.camera = await getPermission('camera')
  }

  async function getPermission(
    permission: keyof typeof permissions,
  ): Promise<RequestPermissionsState> {
    try {
      const result = await navigator.permissions.query({ name: permission as any })
      if (result.state === 'prompt') {
        await result.addEventListener('change', () => {
          permissions[permission] = result.state
        })
      }
      return result.state
    } catch {
      // query is not supported for audio/camera in firefox so set to unsupported
      return 'unsupported'
    }
  }

  return computed(() => {
    if (Object.values(permissions).some((value) => value === 'unsupported')) {
      return 'unsupported'
    }
    if (Object.values(permissions).some((value) => value === 'pending')) {
      return 'pending'
    }
    if (Object.values(permissions).some((value) => value === 'denied')) {
      return 'denied'
    }
    if (Object.values(permissions).some((value) => value === 'prompt')) {
      return 'prompt'
    }
    if (Object.values(permissions).every((value) => value === 'granted')) {
      return 'granted'
    }
    return 'unsupported'
  })
}
